import { Injectable, computed, inject, signal } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { TimeEntry } from '../../features/finance/models/time-entry.model';
import { TimeEntryService } from '../../features/finance/services/time-entry.service';

export interface ActiveTimerState {
  timeEntryId: number;
  projectId: number;
  projectName: string;
  taskId?: number;
  taskName?: string;
  description?: string;
  startedAt: number;          // UTC ms — reset on resume
  pausedAt?: number;          // set when paused, cleared on resume
  accumulatedSeconds: number; // seconds elapsed before current session
  budgetHours?: number;
}

@Injectable({ providedIn: 'root' })
export class ActiveTimerService {
  private readonly STORAGE_KEY = 'ezwork_active_timer';
  private readonly timeEntryService = inject(TimeEntryService);

  readonly activeTimer = signal<ActiveTimerState | null>(null);
  readonly isPaused = computed(() => !!this.activeTimer()?.pausedAt);
  readonly elapsedSeconds = signal(0);
  readonly progressPercent = computed(() => {
    const timer = this.activeTimer();
    const budget = timer?.budgetHours;
    if (!budget) return 0;
    return Math.round((this.elapsedSeconds() / (budget * 3600)) * 100);
  });
  readonly isOverBudget = computed(() => this.progressPercent() >= 90);

  private tickSub?: Subscription;

  constructor() {
    this.restoreFromStorage();
    this.syncWithApi();
  }

  private restoreFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const state = JSON.parse(stored) as ActiveTimerState;
        this.activeTimer.set(state);
        this.updateElapsed();
        if (!state.pausedAt) this.startTicker();
      }
    } catch { /* ignore corrupt storage */ }
  }

  private syncWithApi(): void {
    this.timeEntryService.getActiveTimeEntry().subscribe({
      next: (response) => {
        if (response.hasActiveTimer && response.timeEntry) {
          const entry = response.timeEntry;
          const startTimeStr = entry.startTime.toString();
          const utcStr = startTimeStr.endsWith('Z') ? startTimeStr : `${startTimeStr}Z`;
          const serverStartMs = new Date(utcStr).getTime();
          const accumulatedSeconds = Math.floor((entry.hours || 0) * 3600);

          // Preserve client-side pause state if IDs match
          const prev = this.activeTimer();
          const isSameEntry = prev?.timeEntryId === entry.id;

          const state: ActiveTimerState = {
            timeEntryId: entry.id!,
            projectId: entry.projectId,
            projectName: isSameEntry ? (prev?.projectName ?? `Project #${entry.projectId}`) : `Project #${entry.projectId}`,
            taskId: entry.taskId,
            taskName: isSameEntry ? prev?.taskName : undefined,
            startedAt: isSameEntry && prev?.pausedAt ? prev.startedAt : serverStartMs,
            pausedAt: isSameEntry ? prev?.pausedAt : undefined,
            accumulatedSeconds,
            budgetHours: isSameEntry ? prev?.budgetHours : undefined,
          };

          this.activeTimer.set(state);
          this.saveToStorage(state);
          this.updateElapsed();
          if (!state.pausedAt) this.startTicker();
        } else {
          // API says no active timer — clear everything
          this.activeTimer.set(null);
          this.elapsedSeconds.set(0);
          localStorage.removeItem(this.STORAGE_KEY);
          this.stopTicker();
        }
      },
      error: () => { /* keep localStorage state on network error */ }
    });
  }

  async start(
    projectId: number,
    projectName: string,
    description: string,
    taskId?: number,
    taskName?: string,
    budgetHours?: number
  ): Promise<void> {
    const entry = await firstValueFrom(
      this.timeEntryService.startTimer({ projectId, taskId, description })
    );

    const startTimeStr = entry.startTime.toString();
    const utcStr = startTimeStr.endsWith('Z') ? startTimeStr : `${startTimeStr}Z`;

    const state: ActiveTimerState = {
      timeEntryId: entry.id!,
      projectId,
      projectName,
      taskId,
      taskName,
      description,
      startedAt: new Date(utcStr).getTime(),
      accumulatedSeconds: 0,
      budgetHours,
    };

    this.activeTimer.set(state);
    this.saveToStorage(state);
    this.updateElapsed();
    this.startTicker();
  }

  pause(): void {
    const timer = this.activeTimer();
    if (!timer || timer.pausedAt) return;

    const currentSession = Math.floor((Date.now() - timer.startedAt) / 1000);
    const paused: ActiveTimerState = {
      ...timer,
      accumulatedSeconds: timer.accumulatedSeconds + currentSession,
      pausedAt: Date.now(),
    };

    this.activeTimer.set(paused);
    this.saveToStorage(paused);
    this.stopTicker();
    this.updateElapsed();
  }

  resume(): void {
    const timer = this.activeTimer();
    if (!timer || !timer.pausedAt) return;

    const resumed: ActiveTimerState = {
      ...timer,
      startedAt: Date.now(),
      pausedAt: undefined,
    };

    this.activeTimer.set(resumed);
    this.saveToStorage(resumed);
    this.startTicker();
  }

  async stop(): Promise<TimeEntry> {
    const timer = this.activeTimer();
    if (!timer) throw new Error('No active timer');

    const stoppedEntry = await firstValueFrom(this.timeEntryService.stopTimer(timer.timeEntryId));

    this.activeTimer.set(null);
    this.elapsedSeconds.set(0);
    localStorage.removeItem(this.STORAGE_KEY);
    this.stopTicker();

    return stoppedEntry;
  }

  clearOnLogout(): void {
    this.activeTimer.set(null);
    this.elapsedSeconds.set(0);
    this.stopTicker();
    localStorage.removeItem(this.STORAGE_KEY);
  }

  private updateElapsed(): void {
    const timer = this.activeTimer();
    if (!timer) { this.elapsedSeconds.set(0); return; }

    const elapsed = timer.pausedAt
      ? timer.accumulatedSeconds
      : timer.accumulatedSeconds + Math.floor((Date.now() - timer.startedAt) / 1000);

    this.elapsedSeconds.set(Math.max(0, elapsed));
  }

  private startTicker(): void {
    this.stopTicker();
    this.tickSub = interval(1000).subscribe(() => this.updateElapsed());
  }

  private stopTicker(): void {
    this.tickSub?.unsubscribe();
    this.tickSub = undefined;
  }

  private saveToStorage(state: ActiveTimerState): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
    } catch { /* ignore quota errors */ }
  }
}
